import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback, useState } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '../../components/Icon';
import {
    mdiLoading,
    mdiMagnify,
    mdiEye,
    mdiTrashCan,
    mdiMessageText,
    mdiAccountGroup,
    mdiMessageReplyText,
    mdiCalendarRange,
    mdiRefresh
} from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import Grid, { GridItem } from '@/components/layout/Grid';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CampoData from '@/components/Formulario/CampoData';

type ResponseList = {
    status: number;
    ok?: boolean;
    result: any[];
    total: number;
    cursor: string | number | null;
};

type StatsResponse = {
    ok: boolean;
    result: {
        total_conversas: number;
        total_mensagens: number;
        total_participantes: number;
        conversas_ativas_hoje: number;
    };
};

type ChatInternoPageProps = {};

const ChatInternoPage = ({ }: ChatInternoPageProps) => {
    const [viewObject, setViewObject] = useState<any>(null);
    const [mensagensConversa, setMensagensConversa] = useState<any[]>([]);
    const [loadingMensagens, setLoadingMensagens] = useState(false);

    const [search, setSearch] = useState<string>('');
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    const debouncedSearch = useDebounce(search, 500);

    // Query para estatísticas
    const statsQuery = useQuery<StatsResponse>({
        queryKey: ['chat_interno_stats'],
        queryFn: async () => {
            const response = await axios.get('/api/chat_interno/admin/stats', {
                withCredentials: true,
            });
            return response.data;
        },
    });

    const headers: DataTableHeader[] = [
        {
            label: 'ID',
            width: '60px',
            column: 'id'
        },
        {
            label: 'Título',
            width: '15%',
            render: (row: any) => row.titulo || <span style={{ color: 'var(--tc-color-gray-400)', fontStyle: 'italic' }}>Sem título</span>
        },
        {
            label: 'Criador',
            width: '15%',
            render: (row: any) => row.criador?.nome || '-'
        },
        {
            label: 'Participantes',
            width: '20%',
            render: (row: any) => (
                <div className={styles.participantesList}>
                    {row.participantes?.slice(0, 3).map((p: any) => (
                        <span key={p.id} className={styles.participanteChip}>
                            {p.usuario?.nome?.split(' ')[0]}
                        </span>
                    ))}
                    {row.participantes?.length > 3 && (
                        <span className={styles.participanteChip}>
                            +{row.participantes.length - 3}
                        </span>
                    )}
                </div>
            ),
        },
        {
            label: 'Última Mensagem',
            width: '20%',
            render: (row: any) => row.ultima_mensagem ? (
                <div className={styles.mensagemPreview}>
                    {row.ultima_mensagem.conteudo?.replace(/<@\d+\|([^>]+)>/g, '@$1')}
                </div>
            ) : <span style={{ color: 'var(--tc-color-gray-400)' }}>-</span>
        },
        {
            label: 'Mensagens',
            width: '80px',
            render: (row: any) => row.total_mensagens ?? 0
        },
        {
            label: 'Criado em',
            width: '120px',
            render: (row: any) => row.data_criacao
                ? format(new Date(row.data_criacao), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                : '-'
        },
        {
            label: 'Ações',
            width: '100px',
            stickRight: true,
            alignRight: true,
            render: (row: any) => {
                return (
                    <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
                        <Botao
                            title="Visualizar mensagens"
                            variant="none-primary"
                            icon={mdiEye}
                            onClick={() => handleViewConversa(row)}
                        />
                        <BotaoPerigoso
                            buttonProps={{ title: 'Apagar conversa' }}
                            variant="none-danger"
                            icon={mdiTrashCan}
                            onClick={() => handleDelete(row.id)}
                        />
                    </HBox>
                );
            },
        },
    ];

    const queryList = useInfiniteQuery({
        queryKey: ['chat_interno_admin', debouncedSearch, dataInicio, dataFim],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('txt', debouncedSearch);
            if (pageParam) params.append('c', pageParam);
            if (dataInicio) params.append('data_inicio', dataInicio);
            if (dataFim) params.append('data_fim', dataFim);

            return await axios.get<ResponseList>(
                `/api/chat_interno/admin?${params.toString()}`,
                {
                    withCredentials: true,
                },
            );
        },
        getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    });

    const deleteMutator = useMutation(
        async (id: number) => {
            return await axios.delete(`/api/chat_interno/admin/${id}`, {
                withCredentials: true,
            });
        },
        {
            onSuccess: () => {
                toast('Conversa apagada com sucesso', {
                    toastId: 'del-success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
                queryList.refetch();
                statsQuery.refetch();
            },
            onError: (err: any) => {
                console.error(err);
                toast('Ocorreu um erro ao tentar apagar a conversa.', {
                    toastId: 'del-error',
                    position: 'top-center',
                    type: 'warning',
                    autoClose: 5000,
                });
            },
        },
    );

    const handleViewConversa = async (conversa: any) => {
        setViewObject(conversa);
        setLoadingMensagens(true);

        try {
            const response = await axios.get(`/api/chat_interno/admin/${conversa.id}/mensagens`, {
                withCredentials: true,
            });
            if (response.data.ok) {
                setMensagensConversa(response.data.result);
            }
        } catch (error) {
            console.error('Erro ao carregar mensagens:', error);
            toast('Erro ao carregar mensagens', {
                toastId: 'load-mensagens-error',
                position: 'top-center',
                type: 'error',
                autoClose: 5000,
            });
        } finally {
            setLoadingMensagens(false);
        }
    };

    const handleDelete = (id: number) => {
        deleteMutator.mutate(id);
    };

    const handleCloseModal = () => {
        setViewObject(null);
        setMensagensConversa([]);
    };

    const handleRefresh = () => {
        queryList.refetch();
        statsQuery.refetch();
    };

    const queryFlatList =
        queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
    const total =
        queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;

    const stats = statsQuery.data?.result;

    return (
        <LayoutUsuario>
            <Modal
                instanceId='modal-chat-interno-view'
                title={`Conversa: ${viewObject?.titulo || 'Sem título'}`}
                show={!!viewObject}
                onClose={handleCloseModal}
                hideControls
                size="lg"
            >
                <VBox gap="16px">
                    <HBox gap="16px">
                        <div>
                            <strong>Criador:</strong> {viewObject?.criador?.nome}
                        </div>
                        <div>
                            <strong>Criado em:</strong> {viewObject?.data_criacao
                                ? format(new Date(viewObject.data_criacao), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                                : '-'}
                        </div>
                    </HBox>

                    <div>
                        <strong>Participantes:</strong>
                        <div className={styles.participantesList} style={{ marginTop: '8px' }}>
                            {viewObject?.participantes?.map((p: any) => (
                                <span key={p.id} className={styles.participanteChip}>
                                    {p.usuario?.nome}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div>
                        <strong>Mensagens ({mensagensConversa.length}):</strong>
                        {loadingMensagens ? (
                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                <Icon path={mdiLoading} size={1} spin color="var(--tc-color-gray-500)" />
                            </div>
                        ) : (
                            <div className={styles.mensagensContainer}>
                                {mensagensConversa.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: 'var(--tc-color-gray-400)', padding: '20px' }}>
                                        Nenhuma mensagem nesta conversa.
                                    </div>
                                ) : (
                                    mensagensConversa.map((msg: any) => (
                                        <div key={msg.id} className={styles.mensagemItem}>
                                            <div className={styles.autor}>{msg.autor?.nome}</div>
                                            <div className={styles.conteudo}>
                                                {msg.conteudo?.replace(/<@\d+\|([^>]+)>/g, '@$1')}
                                            </div>
                                            <div className={styles.data}>
                                                {format(new Date(msg.data_criacao), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </VBox>
            </Modal>

            <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
                {/* Header */}
                <Grid>
                    <GridItem sm={12} md={6}>
                        <VBox>
                            <Titulo>Chat Interno</Titulo>
                            <Descricao>Gerencie e monitore as conversas internas dos colaboradores</Descricao>
                        </VBox>
                    </GridItem>
                    <GridItem sm={12} md={6}>
                        <HBox gap="12px" className={styles.headerAcoes}>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiRefresh}
                                onClick={handleRefresh}
                            >
                                Atualizar
                            </Botao>
                        </HBox>
                    </GridItem>
                </Grid>

                {/* Cards de estatísticas */}
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <Icon path={mdiMessageText} size={1.2} color="var(--tc-color-primary)" />
                        <span className={styles.label}>Total de Conversas</span>
                        <span className={styles.value}>{stats?.total_conversas ?? '-'}</span>
                    </div>
                    <div className={styles.statCard}>
                        <Icon path={mdiMessageReplyText} size={1.2} color="var(--tc-color-success)" />
                        <span className={styles.label}>Total de Mensagens</span>
                        <span className={styles.value}>{stats?.total_mensagens ?? '-'}</span>
                    </div>
                    <div className={styles.statCard}>
                        <Icon path={mdiAccountGroup} size={1.2} color="var(--tc-color-info)" />
                        <span className={styles.label}>Usuários Participantes</span>
                        <span className={styles.value}>{stats?.total_participantes ?? '-'}</span>
                    </div>
                    <div className={styles.statCard}>
                        <Icon path={mdiCalendarRange} size={1.2} color="var(--tc-color-warning)" />
                        <span className={styles.label}>Conversas Ativas Hoje</span>
                        <span className={styles.value}>{stats?.conversas_ativas_hoje ?? '-'}</span>
                    </div>
                </div>

                {/* Filtros */}
                <div className={styles.filterSection}>
                    <CampoTexto
                        prepend={
                            <Icon
                                path={mdiMagnify}
                                size={1}
                                color="var(--tc-color-gray-500)"
                            />
                        }
                        placeholder="Pesquisar por título ou participante"
                        value={search}
                        onChange={setSearch}
                    />
                    <CampoData
                        label="Data Início"
                        value={dataInicio}
                        onChange={setDataInicio}
                    />
                    <CampoData
                        label="Data Fim"
                        value={dataFim}
                        onChange={setDataFim}
                    />
                </div>

                {queryList.isFetching && (
                    <div>
                        Aguarde...{' '}
                        <Icon
                            path={mdiLoading}
                            size={0.8}
                            color="var(--tc-color-gray-700)"
                            spin
                        />
                    </div>
                )}
                {!queryList.isFetching && (
                    <div>
                        Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
                        resultados.
                    </div>
                )}

                <DataTable
                    isLoading={queryList.isFetching}
                    cabecalhos={headers}
                    linhas={queryFlatList ?? []}
                    hasMoreData={queryList.hasNextPage}
                    onLoadMore={async () => queryList.fetchNextPage()}
                />
            </VBox>
        </LayoutUsuario>
    );
};

export default ChatInternoPage;
